<template>
  <div class="role-list">
    <div class="filter-bar">
      <input
        v-model="filters.keyword"
        class="filter-input"
        placeholder="角色名称"
        @keyup.enter="loadRoles"
      />
      <select v-model="filters.status" class="filter-select">
        <option value="">全部状态</option>
        <option value="1">启用</option>
        <option value="0">禁用</option>
      </select>
      <button class="btn btn-primary" @click="loadRoles">查询</button>
      <button class="btn btn-default" @click="resetFilters">重置</button>
      <button class="btn btn-success" @click="openEdit()">新增角色</button>
    </div>

    <table class="data-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>角色名称</th>
          <th>角色代码</th>
          <th>权限</th>
          <th>描述</th>
          <th>状态</th>
          <th>创建时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.role_name }}</td>
          <td>{{ item.role_code || '-' }}</td>
          <td>
            <span class="perm-count" @click="showPermissions(item)">{{ formatPermissions(item.permissions).length }} 项</span>
          </td>
          <td>{{ item.description || '-' }}</td>
          <td>
            <span :class="['status-tag', item.status === 1 ? 'enabled' : 'disabled']">
              {{ item.status === 1 ? '启用' : '禁用' }}
            </span>
          </td>
          <td>{{ formatDateTime(item.created_at) }}</td>
          <td>
            <button class="action-btn view" @click="openEdit(item)">编辑</button>
            <button class="action-btn disable" @click="deleteRole(item)">删除</button>
          </td>
        </tr>
        <tr v-if="list.length === 0">
          <td colspan="8" class="empty">暂无数据</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" v-if="total > 0">
      <button class="page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页（共 {{ total }} 条）</span>
      <button class="page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">下一页</button>
    </div>

    <!-- 权限查看弹窗 -->
    <div class="modal-mask" v-if="permVisible" @click.self="permVisible = false">
      <div class="modal">
        <div class="modal-header">
          <span>权限详情 - {{ currentRole.role_name }}</span>
          <button class="modal-close" @click="permVisible = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formatPermissions(currentRole.permissions).length === 0" class="empty">暂无权限</div>
          <div v-else class="perm-list">
            <span v-for="(p, i) in formatPermissions(currentRole.permissions)" :key="i" class="perm-tag">{{ p }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑/新增弹窗 -->
    <div class="modal-mask" v-if="editVisible" @click.self="editVisible = false">
      <div class="modal modal-lg">
        <div class="modal-header">
          <span>{{ editing.id ? '编辑角色' : '新增角色' }}</span>
          <button class="modal-close" @click="editVisible = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>角色名称 <span class="req">*</span></label>
            <input v-model="editing.role_name" class="form-input" placeholder="请输入角色名称" />
          </div>
          <div class="form-item">
            <label>描述</label>
            <input v-model="editing.description" class="form-input" placeholder="请输入角色描述" />
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model="editing.status" class="form-input">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="form-item">
            <label>权限配置</label>
            <div class="perm-tree">
              <div class="perm-group" v-for="group in permissionTree" :key="group.value">
                <div class="perm-group-head">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      :checked="isGroupChecked(group)"
                      :indeterminate.prop="isGroupIndeterminate(group)"
                      @change="toggleGroup(group, $event)"
                    />
                    <span class="group-title">{{ group.label }}</span>
                  </label>
                </div>
                <div class="perm-group-body">
                  <label
                    class="checkbox-label leaf"
                    v-for="child in group.children"
                    :key="child.value"
                  >
                    <input
                      type="checkbox"
                      :value="child.value"
                      v-model="editing.permissionList"
                    />
                    <span>{{ child.label }}</span>
                  </label>
                </div>
              </div>
            </div>
            <span class="form-tip">已选 {{ editing.permissionList.length }} 项权限</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="editVisible = false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveRole">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRoles, createRole, updateRole, deleteRole } from '@/api/system'
import { PERMISSION_TREE } from '@/constants/permissions'

export default {
  name: 'RoleList',
  data() {
    return {
      permissionTree: PERMISSION_TREE,
      list: [],
      total: 0,
      currentPage: 1,
      perPage: 15,
      filters: {
        keyword: '',
        status: ''
      },
      permVisible: false,
      currentRole: {},
      editVisible: false,
      editing: {
        id: null,
        role_name: '',
        description: '',
        status: 1,
        permissionList: []
      },
      saving: false
    }
  },
  mounted() {
    this.loadRoles()
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    }
  },
  methods: {
    async loadRoles() {
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status !== '' ? this.filters.status : undefined
        }
        const res = await getRoles(params)
        this.list = res.data || res.list || []
        this.total = res.total || this.list.length
      } catch (e) {
        this.$message && this.$message.error('加载失败')
      }
    },
    resetFilters() {
      this.filters = { keyword: '', status: '' }
      this.currentPage = 1
      this.loadRoles()
    },
    changePage(page) {
      this.currentPage = page
      this.loadRoles()
    },
    formatDateTime(value) {
      if (!value) return '-'
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    formatPermissions(permissions) {
      if (!permissions) return []
      try {
        const parsed = typeof permissions === 'string' ? JSON.parse(permissions) : permissions
        return Array.isArray(parsed) ? parsed : Object.keys(parsed)
      } catch (e) {
        return []
      }
    },
    groupValues(group) {
      return group.children.map((c) => c.value)
    },
    isGroupChecked(group) {
      const vals = this.groupValues(group)
      return vals.every((v) => this.editing.permissionList.includes(v))
    },
    isGroupIndeterminate(group) {
      const vals = this.groupValues(group)
      const checkedCount = vals.filter((v) => this.editing.permissionList.includes(v)).length
      return checkedCount > 0 && checkedCount < vals.length
    },
    toggleGroup(group, event) {
      const vals = this.groupValues(group)
      if (event.target.checked) {
        const set = new Set(this.editing.permissionList)
        vals.forEach((v) => set.add(v))
        this.editing.permissionList = Array.from(set)
      } else {
        const set = new Set(this.editing.permissionList)
        vals.forEach((v) => set.delete(v))
        this.editing.permissionList = Array.from(set)
      }
    },
    showPermissions(item) {
      this.currentRole = item
      this.permVisible = true
    },
    openEdit(item) {
      if (item) {
        this.editing = {
          id: item.id,
          role_name: item.role_name,
          description: item.description || '',
          status: item.status === undefined ? 1 : item.status,
          permissionList: this.formatPermissions(item.permissions)
        }
      } else {
        this.editing = { id: null, role_name: '', description: '', status: 1, permissionList: [] }
      }
      this.editVisible = true
    },
    async saveRole() {
      if (!this.editing.role_name) {
        this.$message && this.$message.error('请输入角色名称')
        return
      }
      this.saving = true
      try {
        const payload = {
          role_name: this.editing.role_name,
          description: this.editing.description,
          status: this.editing.status,
          permissions: JSON.stringify(this.editing.permissionList)
        }
        if (this.editing.id) {
          await updateRole(this.editing.id, payload)
          this.$message && this.$message.success('更新成功')
        } else {
          await createRole(payload)
          this.$message && this.$message.success('创建成功')
        }
        this.editVisible = false
        this.loadRoles()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || '保存失败'
        this.$message && this.$message.error(msg)
      } finally {
        this.saving = false
      }
    },
    async deleteRole(item) {
      if (!confirm(`确认删除角色「${item.role_name}」？`)) return
      try {
        await deleteRole(item.id)
        this.$message && this.$message.success('删除成功')
        this.loadRoles()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || '删除失败'
        this.$message && this.$message.error(msg)
      }
    }
  }
}
</script>

<style scoped>
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
}
.filter-input,
.filter-select {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.data-table th,
.data-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
}
.data-table th {
  background: #fafafa;
  font-weight: 600;
}
.data-table tr:hover td {
  background: #f9faff;
}
.empty {
  text-align: center;
  color: #999;
  padding: 30px 0;
}
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  display: inline-block;
}
.btn-primary {
  background: #667eea;
  color: #fff;
}
.btn-default {
  background: #eee;
  color: #333;
}
.btn-success {
  background: #2ecc71;
  color: #fff;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.status-tag {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}
.status-tag.enabled {
  background: #e8f8f0;
  color: #2ecc71;
}
.status-tag.disabled {
  background: #fdecea;
  color: #e74c3c;
}
.perm-count {
  margin-right: 8px;
  color: #667eea;
  font-size: 13px;
  cursor: pointer;
}
.perm-count:hover {
  text-decoration: underline;
}
.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}
.action-btn.view {
  background: #3498db;
  color: #fff;
}
.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 16px;
}
.page-btn {
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-info {
  font-size: 14px;
  color: #666;
}
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: #fff;
  border-radius: 8px;
  width: 520px;
  max-width: 92vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-lg {
  width: 720px;
}
.modal-header {
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
  line-height: 1;
}
.modal-body {
  padding: 20px;
  overflow-y: auto;
}
.modal-footer {
  padding: 14px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.perm-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.perm-tag {
  background: #eef1fb;
  color: #667eea;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 13px;
}
.form-item {
  margin-bottom: 16px;
}
.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}
.req {
  color: #e74c3c;
}
.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-tip {
  font-size: 12px;
  margin-top: 4px;
  display: block;
  color: #999;
}
.perm-tree {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  max-height: 360px;
  overflow-y: auto;
}
.perm-group {
  margin-bottom: 14px;
}
.perm-group:last-child {
  margin-bottom: 0;
}
.perm-group-head {
  padding: 6px 8px;
  background: #f7f8fc;
  border-radius: 4px;
}
.group-title {
  font-weight: 600;
  color: #333;
}
.perm-group-body {
  padding: 10px 8px 2px 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #444;
  cursor: pointer;
}
.checkbox-label.leaf {
  width: 45%;
  margin-bottom: 8px;
}
.checkbox-label input {
  cursor: pointer;
}
</style>
